import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../lib/api';
import { portal, fetchDocumentObject, RefereeDetails, ReferenceRow } from '../lib/portal';
import { track } from '../lib/analytics';
import { flattenRefereePhone } from '../lib/phoneDialCodes';
import {
  displayedReferenceRelationship,
  normalizedReferenceRelationshipDetail,
  OTHER_REFERENCE_RELATIONSHIP,
  REFERENCE_RELATIONSHIP_DETAIL_MAX_LENGTH,
} from '../lib/referenceRelationship';
import { useResolvedScheme } from '../lib/theme';
import { ERROR_CODE_MESSAGES } from '../lib/error-codes';

const REQUIRED_SLOTS = 2;
const MAX_SLOTS = 3;

const RELATIONSHIP_OPTIONS = [
  'Team Leader',
  'Supervisor / Manager',
  'Colleague',
  'Teacher / Professor',
  'Coach / Mentor',
  'Other',
];

// The referee form renders through the shared form-engine so its fields — the
// telephone country-code dropdown especially — match every other form on the
// platform. It is NOT an application step (it lives in a slot card with its own
// Send/Cancel actions), so it drives the engine directly with a local
// fieldsConfig and custom action labels (engine v2.24.0 submitLabel/backLabel).
const FormEnginePortable = lazy(() =>
  import('@alliance-strategies/form-engine-portable').then((m: any) => ({
    default: (m.FormEnginePortable ?? m.default) as React.ComponentType<Record<string, unknown>>,
  })),
);

export const REFEREE_FIELDS_CONFIG = {
  layout: {
    type: 'single_page',
    // Strip the engine's default white-card chrome so it blends into the slot.
    containerClass: 'referee-engine',
    innerClass: 'referee-engine-inner',
    fields: [
      { name: 'refereeName', label: 'Full Name of Referee', type: 'text', required: true, colSpan: 'half', placeholder: "Enter referee's full name" },
      {
        name: 'refereeRelationship',
        label: 'Relationship to You',
        type: 'select',
        required: true,
        colSpan: 'half',
        options: RELATIONSHIP_OPTIONS.map((o) => ({ value: o, label: o })),
      },
      {
        name: 'refereeRelationshipDetail',
        label: 'Relationship details',
        type: 'text',
        required: false,
        maxLength: REFERENCE_RELATIONSHIP_DETAIL_MAX_LENGTH,
        colSpan: 'half',
        behavior: {
          version: 1,
          visibleWhen: { field: 'refereeRelationship', operator: 'equals', value: OTHER_REFERENCE_RELATIONSHIP },
          requiredWhen: { field: 'refereeRelationship', operator: 'equals', value: OTHER_REFERENCE_RELATIONSHIP },
          clearWhenHidden: true,
          readOnlyDisplay: { inlineWithControllerValue: true, separator: ' — ' },
        },
      },
      { name: 'refereeOrganization', label: 'Organization/Company Name', type: 'text', required: true, colSpan: 'half', placeholder: 'Enter organization name' },
      { name: 'refereeJobTitle', label: 'Job Title', type: 'text', required: true, colSpan: 'half', placeholder: "Enter referee's job title" },
      { name: 'refereeEmail', label: 'Referee Email Address', type: 'email', required: true, colSpan: 'half', placeholder: 'referee@email.com' },
      { name: 'confirmEmail', label: 'Confirm Referee Email Address', type: 'email', required: true, colSpan: 'half', placeholder: 'Confirm email address' },
      { name: 'refereePhone', label: 'Telephone Number', type: 'phone', required: true, colSpan: 'half', placeholder: 'Enter phone number' },
      {
        name: 'knownSixMonths',
        label: 'I confirm this referee has known me for at least 6 months.',
        type: 'checkbox',
        required: true,
        colSpan: 'full',
      },
    ],
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Cross-field + format checks the engine's built-in required gate doesn't cover.
// Runs as an engine validation plugin so failures block submit and render inline
// on the offending field (InputTypeFieldRenderer surfaces validationErrors).
export function refereeFormValidation(values: Record<string, any>): Record<string, string> {
  const errors: Record<string, string> = {};
  const email = String(values.refereeEmail ?? '').trim();
  const confirm = String(values.confirmEmail ?? '').trim();
  if (email && !EMAIL_RE.test(email)) errors.refereeEmail = 'Enter a valid email address';
  if (confirm && !EMAIL_RE.test(confirm)) errors.confirmEmail = 'Enter a valid email address';
  if (email && confirm && email.toLowerCase() !== confirm.toLowerCase())
    errors.confirmEmail = "The email addresses don't match — the request is sent to this address.";
  if (values.knownSixMonths !== true)
    errors.knownSixMonths = 'Confirm that this referee has known you for at least 6 months';
  if (values.refereeRelationship === OTHER_REFERENCE_RELATIONSHIP) {
    const detail = normalizedReferenceRelationshipDetail(values.refereeRelationship, values.refereeRelationshipDetail);
    if (!detail) errors.refereeRelationshipDetail = 'Relationship details are required';
    else if (detail.length > REFERENCE_RELATIONSHIP_DETAIL_MAX_LENGTH)
      errors.refereeRelationshipDetail = `Relationship details must be ${REFERENCE_RELATIONSHIP_DETAIL_MAX_LENGTH} characters or fewer`;
  }
  return errors;
}

export function refereeDetailsPayload(data: Record<string, any>): RefereeDetails {
  const relationship = String(data.refereeRelationship ?? '');
  const relationshipDetail = normalizedReferenceRelationshipDetail(relationship, data.refereeRelationshipDetail);
  return {
    refereeName: String(data.refereeName ?? '').trim(),
    refereeEmail: String(data.refereeEmail ?? '').trim(),
    refereeRelationship: relationship,
    ...(relationshipDetail ? { refereeRelationshipDetail: relationshipDetail } : {}),
    refereeOrganization: String(data.refereeOrganization ?? '').trim(),
    refereeJobTitle: String(data.refereeJobTitle ?? '').trim(),
    refereePhone: flattenRefereePhone(data.refereePhone),
    knownSixMonths: data.knownSixMonths === true,
  };
}

/**
 * Reference Details — the applicant side of reference collection. Unlike
 * every other step this is NOT a form-engine step: each reference is a
 * Globus-owned request row with its own lifecycle (invite → referee submits
 * → staff review), so the step renders slot cards with per-row state and
 * relays actions (Send Request / Resend / Delete & Replace / View PDF).
 * Step completion itself still flows from Globus step tracking — the step
 * auto-completes when enough references are APPROVED.
 *
 * State mapping (status + reviewStatus → card):
 *   (no row)                     Not Started — referee details form
 *   invited | reminded           Request Sent — awaiting referee, resend/replace
 *   completed + pending_review   Submitted — pending approval, locked
 *   completed + approved         Completed — green banner + View PDF
 *   completed + rejected         Wasn't Approved — red banner + Delete & Replace
 *   expired                      Referee didn't respond — resend is gone, replace
 *   revoked                      (hidden — superseded by its replacement row)
 */
export default function ReferenceDetailsStep({
  appId,
  isLast = false,
  readOnly = false,
  canSubmit = !readOnly,
  onContinue,
  onBack,
}: {
  appId: string | number;
  /** Final step of the whole application — label flips to "Save", matching
   *  the engine's button semantics. */
  isLast?: boolean;
  /** Authoritative step-access edit capability. Existing details remain
   * visible, but create/resend/replace controls are suppressed when true. */
  readOnly?: boolean;
  /** Authoritative step-access submit capability for Save & Continue. */
  canSubmit?: boolean;
  /** Advance to the next step in display order (ApplicationPage wiring). */
  onContinue?: () => void;
  /** Footer Back — plain navigation, matching the engine's footer on
   *  every other step. Nothing to flush here: reference rows persist the
   *  moment each request is sent. Forward progression is Save & Continue
   *  only (Asana 1217761042299537). */
  onBack?: () => void;
}) {
  const [rows, setRows] = useState<ReferenceRow[]>();
  const [loadError, setLoadError] = useState<string>();

  const reload = () =>
    portal
      .references(appId)
      .then((r) => {
        setRows(r);
        setLoadError(undefined);
      })
      .catch(() => setLoadError("We couldn't load your references. Please refresh the page."));

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  // Revoked rows are superseded history — the replacement row owns the slot.
  const active = useMemo(
    () =>
      (rows ?? [])
        .filter((r) => r.status !== 'revoked')
        .sort((a, b) => new Date(a.invitedAt).getTime() - new Date(b.invitedAt).getTime()),
    [rows],
  );
  const [showOptional, setShowOptional] = useState(false);
  // A rejected or expired reference occupies its slot (its card carries the
  // explanation + Delete & Replace), but it can never satisfy the requirement
  // — only VIABLE rows (anything still capable of ending approved) count
  // toward it. When non-viable rows would otherwise fill every visible slot,
  // open one extra empty slot (up to MAX_SLOTS) so the applicant ALWAYS has a
  // direct "add a replacement" path (bug #4), on top of per-row replace.
  const isViable = (r: ReferenceRow) =>
    r.status !== 'expired' && !(r.status === 'completed' && r.reviewStatus === 'rejected');
  const viableCount = active.filter(isViable).length;
  const needReplacementSlot = viableCount < REQUIRED_SLOTS && active.length >= REQUIRED_SLOTS;
  const slotCount = Math.min(
    MAX_SLOTS,
    Math.max(REQUIRED_SLOTS, active.length + (showOptional || needReplacementSlot ? 1 : 0)),
  );

  if (loadError) return <div className="globus-banner err" role="alert">{loadError}</div>;
  if (!rows) return <div className="card formcard skeleton">Loading references…</div>;

  return (
    <div className="card formcard references-step">
      <header className="formhead">
        <h2>Reference Details</h2>
        <p className="muted">We'd love to hear what others say about you, submit your reference details.</p>
      </header>
      <p>
        Add your referee's details below and we'll send them a reference form to fill out. Once they submit it
        and it's approved, their reference will show up here automatically. You'll need {REQUIRED_SLOTS} approved
        references to complete your application.
      </p>

      {Array.from({ length: slotCount }, (_, i) => (
        <ReferenceSlot
          key={active[i]?.id ?? `empty-${i}`}
          appId={appId}
          index={i}
          row={active[i]}
          // The 3rd slot is only "(optional)" when the requirement is already
          // covered by viable references — when it exists as a replacement
          // path for a rejected/expired reference it is anything but.
          optional={i >= REQUIRED_SLOTS && viableCount >= REQUIRED_SLOTS}
          readOnly={readOnly}
          onChanged={reload}
        />
      ))}

      {!readOnly && active.length >= slotCount && slotCount < MAX_SLOTS && !showOptional && !needReplacementSlot && (
        <p>
          <button type="button" className="linklike" onClick={() => setShowOptional(true)}>
            + Add Optional Reference ({MAX_SLOTS}rd Reference)
          </button>
        </p>
      )}

      {/* Everything on this step is already saved the moment a request is
          sent — but applicants expect the same Save & Continue affordance as
          every other step, and without it they don't trust that their
          references were kept. The button is reassurance + navigation. */}
      {(onBack || (onContinue && canSubmit)) && (
        <div className="refcontinue">
          <span className="muted">Your reference details are saved automatically when you send each request.</span>
          <span style={{ display: 'flex', gap: 12 }}>
            {onBack && (
              <button type="button" className="ghost" onClick={onBack} style={{ width: 'auto' }} data-testid="button-step-back">
                Back
              </button>
            )}
            {onContinue && canSubmit && (
              <button type="button" onClick={onContinue} style={{ width: 'auto' }} data-testid="button-references-continue">
                {isLast ? 'Save' : 'Save & Continue'}
              </button>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

function badgeFor(row?: ReferenceRow): { label: string; cls: string; state: string } {
  if (!row) return { label: 'Not Started', cls: '', state: 'Not Started' };
  if (row.status === 'invited' || row.status === 'reminded')
    return { label: 'Request Sent', cls: 'warn', state: 'Awaiting Reference Submission' };
  if (row.status === 'expired')
    return { label: 'No Response', cls: 'err', state: 'Referee didn’t respond' };
  if (row.status === 'completed') {
    if (row.reviewStatus === 'approved') return { label: 'Reference Completed', cls: 'ok', state: 'Approved' };
    if (row.reviewStatus === 'rejected') return { label: 'Not Approved', cls: 'err', state: 'Rejected' };
    return { label: 'Reference Submitted', cls: '', state: 'Pending Approval' };
  }
  return { label: row.status, cls: '', state: row.status };
}

export function formatReferenceEmailSentAt(
  value: string,
  locale?: Intl.LocalesArgument,
  timeZone?: string,
): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(locale, {
    dateStyle: 'long',
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {}),
  });
}

function ReferenceSlot({
  appId,
  index,
  row,
  optional,
  readOnly,
  onChanged,
}: {
  appId: string | number;
  index: number;
  row?: ReferenceRow;
  /** Renders the "(optional)" heading suffix — false for a 3rd slot opened as
   *  the replacement path for a rejected/expired reference. */
  optional?: boolean;
  readOnly: boolean;
  onChanged: () => void;
}) {
  const [replacing, setReplacing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [relationshipDetailError, setRelationshipDetailError] = useState<string>();
  const badge = badgeFor(row);
  const emailSentAtFormatted = row?.emailSentAt
    ? formatReferenceEmailSentAt(row.emailSentAt)
    : null;

  const submitDetails = async (details: RefereeDetails) => {
    setBusy(true);
    setError(undefined);
    setRelationshipDetailError(undefined);
    try {
      if (row && replacing) {
        await portal.replaceReference(appId, row.id, details);
        track('reference_replaced', {});
      } else {
        await portal.createReference(appId, details);
        track('reference_requested', {});
      }
      setReplacing(false);
      onChanged();
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined;
      if (code === 'RELATIONSHIP_DETAIL_REQUIRED') {
        setRelationshipDetailError('Relationship details are required');
      } else if (code === 'RELATIONSHIP_DETAIL_TOO_LONG') {
        setRelationshipDetailError(`Relationship details must be ${REFERENCE_RELATIONSHIP_DETAIL_MAX_LENGTH} characters or fewer`);
      }
      // Classify on the structured ApiError.code, not a message regex — the
      // same bug class TermsGateModal's agreement-stale check had (a wording
      // edit on the Hub side silently stops a regex matching; a code can't
      // drift the same way).
      const knownMessage = code ? (ERROR_CODE_MESSAGES as Record<string, string>)[code] : undefined;
      setError(
        code === 'RELATIONSHIP_DETAIL_REQUIRED' || code === 'RELATIONSHIP_DETAIL_TOO_LONG'
          ? undefined
          : (knownMessage ?? "We couldn't send the request. Please check the details and try again."),
      );
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!row) return;
    setBusy(true);
    setError(undefined);
    try {
      await portal.resendReference(appId, row.id);
      setNotice(`We've re-sent the reference request to ${row.refereeEmail}.`);
      track('reference_resent', {});
      onChanged();
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined;
      const knownMessage = code ? (ERROR_CODE_MESSAGES as Record<string, string>)[code] : undefined;
      setError(knownMessage ?? "We couldn't resend the request right now. Please try again later.");
    } finally {
      setBusy(false);
    }
  };

  const viewPdf = async () => {
    if (!row) return;
    try {
      const { url } = await fetchDocumentObject(`/api/v1/references/${encodeURIComponent(row.id)}/pdf`);
      window.open(url, '_blank', 'noopener');
    } catch {
      setError("We couldn't open the reference letter. Please try again.");
    }
  };

  const showForm = (!row || replacing) && !readOnly;
  const canReplace =
    !readOnly &&
    row &&
    (row.status === 'invited' ||
      row.status === 'reminded' ||
      row.status === 'expired' ||
      (row.status === 'completed' && row.reviewStatus === 'rejected'));
  const canResend = !readOnly && row && (row.status === 'invited' || row.status === 'reminded');

  return (
    <section className="reference-slot" aria-label={`Reference ${index + 1}`}>
      <header className="refhead">
        <h3>
          Reference {index + 1}
          {optional ? ' (optional)' : ''}
        </h3>
        <span className={`refbadge ${badge.cls}`}>{badge.label}</span>
        <span className="muted refstate">{badge.state}</span>
      </header>

      {row?.status === 'completed' && row.reviewStatus === 'rejected' && (
        <div className="globus-banner err" role="alert">
          <b>This Reference Wasn't Approved.</b> Unfortunately, this reference did not meet our guidelines.
          {row.reviewNotes ? ` ${row.reviewNotes}` : ''} Don't worry — you can replace it with a new referee below to
          keep things moving forward.
        </div>
      )}
      {row?.status === 'completed' && row.reviewStatus === 'approved' && (
        <div className="globus-banner ok" role="status">
          <b>Reference Complete.</b> Great news! Your referee's reference has been received, reviewed, and approved —
          it's now officially part of your application.
        </div>
      )}
      {row?.status === 'expired' && (
        <div className="globus-banner err" role="alert">
          Your referee didn't respond before the request expired. Add a new referee (or the same one again) below to
          send a fresh request.
        </div>
      )}
      {notice && <div className="globus-banner ok" role="status">{notice}</div>}
      {error && <div className="globus-banner err" role="alert">{error}</div>}

      {showForm ? (
        <RefereeForm
          submitLabel={replacing ? 'Send New Request' : 'Send Request'}
          onCancel={replacing ? () => setReplacing(false) : undefined}
          onSubmit={submitDetails}
          relationshipDetailError={relationshipDetailError}
        />
      ) : row ? (
        <>
          <dl className="refdetails">
            <div>
              <dt>Full Name of Referee</dt>
              <dd>{row!.refereeName}</dd>
            </div>
            <div>
              <dt>Relationship to You</dt>
              <dd>{displayedReferenceRelationship(row!.refereeRelationship, row!.refereeRelationshipDetail)}</dd>
            </div>
            <div>
              <dt>Referee Email Address</dt>
              <dd>{row!.refereeEmail}</dd>
            </div>
            <div>
              <dt>Telephone Number</dt>
              <dd>{row!.refereePhone ?? '—'}</dd>
            </div>
          </dl>
           {emailSentAtFormatted && (
             <p className="muted">
               We’ve emailed {row.refereeName} on {emailSentAtFormatted}.
             </p>
           )}
          <p className="refactions">
            {row!.status === 'completed' && row!.reviewStatus === 'approved' && (
              <button type="button" onClick={viewPdf} style={{ width: 'auto' }}>
                View PDF
              </button>
            )}
            {canResend && (
              <button type="button" disabled={busy} onClick={resend} style={{ width: 'auto' }}>
                Resend Request
              </button>
            )}
            {canReplace && (
              <button type="button" className="danger" disabled={busy} onClick={() => setReplacing(true)} style={{ width: 'auto' }}>
                Delete &amp; Replace
              </button>
            )}
            {row!.status === 'completed' && row!.reviewStatus === 'pending_review' && (
              <span className="muted">Your referee's response is being reviewed by our team.</span>
            )}
          </p>
        </>
      ) : (
        <p className="muted">No reference has been added.</p>
      )}
    </section>
  );
}

function RefereeForm({
  submitLabel,
  onCancel,
  onSubmit,
  relationshipDetailError,
}: {
  submitLabel: string;
  onCancel?: () => void;
  onSubmit: (details: RefereeDetails) => Promise<void> | void;
  relationshipDetailError?: string;
}) {
  // The plugin must ride on the engine's OWN registry object (the engine
  // iterates registry.customComponents etc.), so build it from the exported
  // createEmptyRegistry once the package chunk loads.
  const [registry, setRegistry] = useState<{ validationPlugins: unknown[] } | null>(null);
  const scheme = useResolvedScheme();
  useEffect(() => {
    let alive = true;
    import('@alliance-strategies/form-engine-portable').then((m: any) => {
      if (alive) setRegistry({ ...m.createEmptyRegistry(), validationPlugins: [refereeFormValidation] });
    });
    return () => {
      alive = false;
    };
  }, []);

  // The engine renders (and validates) the fields; on completion it hands back
  // the values. Flatten the phone object ({countryCode, number}) to the single
  // string Globus stores, and map to the referee payload.
  const handleComplete = async (data: Record<string, any>) => {
    await onSubmit(refereeDetailsPayload(data));
  };

  if (!registry) return <div className="card formcard skeleton">Loading form…</div>;

  return (
    <Suspense fallback={<p>Loading form…</p>}>
      <FormEnginePortable
        theme={scheme}
        stepId="referee_details"
        hideTitle
        additionalReferences={false}
        registry={registry}
        submitLabel={submitLabel}
        {...(onCancel ? { onBack: onCancel, backLabel: 'Cancel' } : {})}
        stepConfig={{ stepKey: 'referee_details', name: 'Referee Details', fieldsConfig: REFEREE_FIELDS_CONFIG }}
        stepProgress={{ status: 'not_started', stepData: {}, step_data: {} }}
        initialFormData={{}}
        onSave={async () => {}}
        onComplete={handleComplete}
        onError={() => {}}
      />
      {relationshipDetailError && (
        <p className="field-error" role="alert">
          Relationship details: {relationshipDetailError}
        </p>
      )}
    </Suspense>
  );
}
